import {request} from './request';
import * as PATHS from '@/constants/path';
import {ForgotPasswordRequest} from '@/typings/api/forgotPassword';
import {ResetPasswordRequest} from '@/typings/api/resetPassword';
import {URLParamBuilder} from '@/utils/functions';
import {
  CreateUserRequest,
  DeleteUserIntegrationRequest,
  DeleteUserRequest,
  FetchAllUsersRequest,
  FetchTimeZonesRequest,
  FetchUserRequest,
  MyProfileRequest,
  RolesRequest,
  UpdateUserSettingRequest,
  UserSettingRequest,
} from '@/typings/api/user';
import {API} from './typings';
import {UpdateUserRequest} from '@/typings/api/emailAutomationRequest';

/** Login interface | POST /login */
export function loginRequest(body: API.LoginRequestParams, options?: { [key: string]: any }) {
  return request(PATHS.LOGIN_API_PATH, {
    method: 'POST',
    data: {
      Customer: body
    },
    ...(options || {}),
  });
}

export function loginAccessTokenRequest(body: API.LoginParams) : any {
  return request(PATHS.LOGIN_ACCESS_TOKEN_API_PATH, {
    method: 'POST',
    data: body,
    noAuthHeader: true
  });
}

export function confirmOtpCodeRequest(body) : any {
  return request(PATHS.CONFIRM_OTP_CODE, {
    method: 'POST',
    data: body,
    noAuthHeader: true
  });
}

/** Microsoft Entra SSO: ask the backend for the consent/sign-in URL.
 * The frontend never builds OAuth URLs itself (backend owns the exchange). */
export function requestMicrosoftSsoRedirect(body: { redirect?: string } = {}): any {
  return request(PATHS.SSO_MICROSOFT_LOGIN_API_PATH, {
    method: 'POST',
    data: body,
    noAuthHeader: true
  });
}

/** Microsoft Entra SSO: trade the backend's single-use exchange code for our
 * own token pair. noAuthHeader is load-bearing — a stale Authorization header
 * would send a failure into the global 401 handler, which wipes credentials
 * and hard-redirects (request.ts). */
export function exchangeSsoCodeRequest(body: { code: string }): any {
  return request(PATHS.SSO_EXCHANGE_API_PATH, {
    method: 'POST',
    data: body,
    noAuthHeader: true
  });
}

export function refreshTokenRequest(body: API.RefreshTokenParams) {
  return request(PATHS.LOGIN_REFRESH_TOKEN_API_PATH, {
    method: 'POST',
    data: body,
    noAuthHeader: true
  });
}



export function fetchMeRequest() {
  return request(PATHS.GET_ME_API_PATH, {
    method: 'GET',
    data: {}
  });
}

export function fetchPartners() {
  return request(PATHS.GET_PARTNERS, {
    method: 'GET'
  });
}

export function fetchUserSettingsRequest() {
  return request<UserSettingRequest["response"]>(PATHS.GET_SETTINGS_API_PATH, {
    method: 'GET'
  });
}
export function fetchUserRequest(
  { params = { userId: "" }, query = {} }) {
    const finalUrl = URLParamBuilder(PATHS.GET_USER_API_PATH, {
      params: {userId: params.userId},
      queryString: query
    },false)

  return request<FetchUserRequest["response"]>(finalUrl, {
    method: 'GET'
  });
}
export function fetchAllUsersRequest(
  { query, url }) {
  const finalUrl = url || URLParamBuilder(PATHS.GET_ALL_USER_API_PATH, {
      queryString: query,
    },false)
  return request<FetchAllUsersRequest["response"]>(finalUrl, {
    method: 'GET'
  });
}
export function fetchUserBranches(
  { query = {}, ...options }) {
  const finalUrl = URLParamBuilder(PATHS.GET_USER_BRANCHES_API_PATH, {
    queryString: query,
  }, false)
  return request<FetchAllUsersRequest["response"]>(finalUrl, {
    method: 'GET',
    ...options
  });
}


type ChangeUserSettingsRequestOptions = {
  payload: UpdateUserSettingRequest['payload']
}
export function changeUserSettingsRequest({ payload }: ChangeUserSettingsRequestOptions) {
  return request<UpdateUserSettingRequest["response"]>(PATHS.GET_SETTINGS_API_PATH, {
    method: 'POST',
    data: payload
  });
}


type ForgotPasswordRequestOptions = {
  payload: ForgotPasswordRequest["payload"]
}

export function forgotPasswordRequest({payload}: ForgotPasswordRequestOptions) {
  return request<ForgotPasswordRequest["response"]>(PATHS.FORGOT_PASSWORD_API_PATH, {
    method: 'POST',
    data: payload,
  });
}


type ResetPasswordRequestOptions = {
  payload: ResetPasswordRequest["payload"],
  params: ResetPasswordRequest["parameters"],
}

export function resetPasswordRequest({payload, params}: ResetPasswordRequestOptions) {
  const url = URLParamBuilder(PATHS.RESET_PASSWORD_API_PATH, {
    params
  })
  return request<ResetPasswordRequest["response"]>(url, {
    method: 'POST',
    data: payload,
  });
}

type CreateUserRequestOptions = {
  payload: CreateUserRequest["payload"]
}
export function createUserRequest({ payload }: CreateUserRequestOptions) {
  const url = PATHS.CREATE_USER_API_PATH
  return request<CreateUserRequest["response"]>(url, {
    method: 'POST',
    data: payload,
  });
}

type UpdateUserOptions = {
  params: UpdateEmailAutomationRequest["parameters"]
  payload: UpdateEmailAutomationRequest["payload"]
}
export function updateUserRequest({ params, payload }: UpdateUserOptions) {
const finalUrl = URLParamBuilder(PATHS.UPDATE_USER_API_PATH, {
  params
})
return request<UpdateUserRequest["response"]>(finalUrl, {
  method: 'PATCH',
  data: payload,
});
}

export function updateCustomAvailabilityRequest({ params, payload }: { params: { userId: string, branchServiceId: string }, payload: any }) {
  const finalUrl = URLParamBuilder(PATHS.UPDATE_CUSTOM_AVAILABILITY_API_PATH, {
    params
  });
  return request(finalUrl, {
    method: 'POST',
    data: payload,
  });
}

export function deleteCustomAvailabilityRequest({ params }: { params: { userId: string, branchServiceId: string } }) {
  const finalUrl = URLParamBuilder(PATHS.DELETE_CUSTOM_AVAILABILITY_API_PATH, {
    params,
  });
  return request(finalUrl, {
    method: 'DELETE',
  });
}
type DeleteUserOptions = {
  params: DeleteUserRequest["parameters"]
}
export function deleteUserRequest({params}: DeleteUserOptions) {
  const url = URLParamBuilder(PATHS.DELETE_USER_API_PATH, {
    params
  })
  return request<DeleteUserRequest["response"]>(url, {
    method: 'DELETE'
  });
}

type DeleteUserIntegrationOptions = {
  params: DeleteUserIntegrationRequest["parameters"]
}
/** DELETE /users/:userId/integrations/:integrationType — removes that type's rows for the user. */
export function deleteUserIntegrationRequest({params}: DeleteUserIntegrationOptions) {
  const url = URLParamBuilder(PATHS.DELETE_USER_INTEGRATION_API_PATH, {
    params
  })
  return request<DeleteUserIntegrationRequest["response"]>(url, {
    method: 'DELETE'
  });
}

export function bulkDeleteUsersRequest({ ids }: { ids: string[] }) {
  return request(PATHS.GET_ALL_USER_API_PATH, { method: 'DELETE', data: { ids } });
}

/** POST /users/bulk — adds or removes skills (slugs) on the users in `ids`. */
export function bulkUpdateUsersRequest(data: { action: 'add_skills' | 'remove_skills'; ids: string[]; value: string[] }) {
  return request(PATHS.BULK_USERS_API_PATH, { method: 'POST', data });
}
export function fetchConversionalRolesRequest(query?: Record<string, any>) {
  const finalUrl = URLParamBuilder(PATHS.GET_ROLES_API_PATH, {
    queryString: query,
  }, false)
  return request<RolesRequest["response"]>(finalUrl, {
    method: 'GET'
  });
}
export function fetchAvailableTimezones() {
  return request<FetchTimeZonesRequest["response"]>(PATHS.GET_AVAILABLE_TIME_ZONES, {
    method: 'GET'
  });
}

type EditMyProfileRequestOptions = {
  payload: MyProfileRequest["payload"]
}
export function editMyProfileRequest({ payload }: EditMyProfileRequestOptions) {
  const url = PATHS.EDIT_MY_PROFILE
  return request<MyProfileRequest["response"]>(url, {
    method: 'POST',
    data: payload,
  });
}

export function disableUserRequest({params}: {params: {userId: string}}) {
  const url = URLParamBuilder(PATHS.DISABLE_USER_API_PATH, {
    params
  })
  return request<DeleteUserRequest["response"]>(url, {
    method: 'PATCH'
  });
}

export function enableUserRequest({params}: {params: {userId: string}}) {
  const url = URLParamBuilder(PATHS.ENABLE_USER_API_PATH, {
    params
  })
  return request(url, {
    method: 'PATCH'
  });
}

type UploadAvatarRequestOptions = {
  payload: FormData
}
export function uploadAvatarRequest({ payload }: UploadAvatarRequestOptions) {
  return request(PATHS.UPLOAD_AVATAR, {
    method: 'POST',
    data: payload,
  });
}
